/* eslint-disable no-secrets/no-secrets */
import { OriginAddToCart } from '../models/events/add-to-cart.model';
import { OriginPageHit, ZenitExperiment, ZenitExperiments } from '../models/events/page-hit-event.model';
import {
    ItemTypes,
    ZenitList,
    Section,
    ZenitEventData,
    ZenitEventName,
    ZenitEventOrigin,
    ZenitEventValues,
    ZenitItem,
    ZenitPageType,
    ZenitPersonalizationParam,
    ZenitZoomLevel,
    ZenitClickOrigin,
} from '../models/zenit-general.model';

import { CartItem } from '@api/cart/cart.models';
import { Item } from '@api/catalog/items/medias/items.models';
import { Product } from '@api/catalog/products/products.models';
import { GiftCard } from '@api/gift-card/models/gift-card.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { EmpathySearcherOrigin } from '@core/domain/types/empathy-searcher-origin.type';
import { PageSection } from '@core/domain/types/page-sections.types';
import { limitQuerySearch } from '@features/search/domain/constants/search.constant';
import { QueryResult, SearchType } from '@features/search/domain/models/search.models';
import { OptimizelyData, OptimizelyState, OptimizelyCampaignsState, OptimizelyExperiment } from '@shared/ab-testing/models/optimizely.models';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { eventFromParser } from '@shared/analytics/COMMON/services/analytics.utils';
import { SearchRequestMethodName } from '@shared/analytics/store/analytics.state';
import { SearchRequestFilter } from '@shared/analytics/ZENIT/models/events/search-assistant.model';
import { RelatedsPlace } from '@shared/product/related-products/application/store/related-products.state';
import { ProductUid, ProductViewPlace, RelatedsProvider } from '@shared/product/related-products/domain/models/related-products.models';
import { GridType } from '@shared/ui/models/grid-type.model';
import { ModalData } from '@ui/dialog/store/modal.state';

const RelatedPlaces = [
    EventFrom.addToCartModal,
    EventFrom.backSoon,
    EventFrom.cartYoda,
    EventFrom.comingSoon,
    EventFrom.completeLook,
    EventFrom.completeLookYoda,
    EventFrom.frecuentlyBoughtTogether,
    EventFrom.othersYouMayLike,
    EventFrom.recentlyViewed,
    EventFrom.relateds,
    EventFrom.relatedsBuyed,
    EventFrom.similar,
    EventFrom.similaresYoda,
    EventFrom.topSales,
];

/**
 * Which part of the interface was the origin of this search results page
 * This property should be populated for pagehit events with pagetype=PRODUCT_SEARCH_RESULTS
 * Must be one of the enum list
 *
 * @param origin
 * @returns OriginPageHit
 */
const getSearchOrigin = (origin: EmpathySearcherOrigin | SearchType): OriginPageHit => {
    // No están contemplados todos los casos debido a las limitaciones de lo que nos llega con el buscador de Empathy
    switch (origin) {
        case 'linked':
            return OriginPageHit.manual;
        case 'empathize_next_queries':
        case 'next_query_grid':
            return OriginPageHit['next-queries'];
        case 'empathize_category':
        case 'empathize_term':
        case 'busqueda_sugerida':
            return OriginPageHit.autocomplete;
        case 'empathize_trending':
        case 'trending':
            return OriginPageHit['top-queries'];
        case 'history':
        case 'ultimas_busquedas':
            return OriginPageHit['previous-queries'];
        case 'related_tag':
        case 'busqueda_relacionada':
            return OriginPageHit['related-tags'];
        default:
            return OriginPageHit.manual;
    }
};

/* ## Funciones para calcular el índice de un item en listas ## */

export const getQueryResultsIndex = <T>(
    item: T & { id?: number | string; productId?: string },
    queryResults: QueryResult[],
): number => {
    return queryResults?.find(product => product.id === item?.id)?.position;
};

export const getProductGridIndex = (product: Product, filteredProductsIds: number[] = []): number => {
    return product?.relatedType ? -1 : filteredProductsIds.indexOf(parseInt(product?.id));
};

export const getItemListIndex = <T>(
    item: T & { id?: number | string; productId?: string },
    itemList: Array<T & { id?: number | string; productId?: string }> = []
): number => {
    const itemId = typeof item?.id === 'number' ? item.id?.toString() : item?.id;
    const foundItem = itemList.find((_item) =>
        (_item?.id?.toString() || _item.productId) === (itemId || item?.productId));
    return itemList.indexOf(foundItem);
};

export const getRelatedProduct = (
    item: { id?: number | string; productId?: string; moca?: string },
    place: ProductViewPlace,
    relatedsSectionId: string,
    relatedProductsPlaces: Record<ProductViewPlace, RelatedsPlace>
): ProductUid => {
    const carousel = relatedProductsPlaces?.[place]
        ?.carousels?.[relatedsSectionId];
    return carousel?.filteredItemsIds.find((prodUid) => {
        return item.id === prodUid?.product_id || item.moca === prodUid?.uid?.slice(1);
    });
};

export const getRelatedProductIndex = (
    item: { id?: number | string; productId?: string; moca?: string },
    place: ProductViewPlace,
    relatedsSectionId: string,
    relatedProductsPlaces: Record<ProductViewPlace, RelatedsPlace>
): number => {
    const carousel = relatedProductsPlaces?.[place]
        ?.carousels?.[relatedsSectionId];
    return carousel?.filteredItemsIds.findIndex((prodUid) => {
        return item.id === prodUid?.product_id || item.moca === prodUid?.uid?.slice(1);
    });
};

export const getProductItemIndex = <T>(
    item: T & { id?: number | string; productId?: string },
    eventFrom: EventFrom,
    values: ZenitEventValues,
    eventData: ZenitEventData
): number | null => {
    let index: number;
    switch (eventFrom) {
        case EventFrom.searchResults:
            return getQueryResultsIndex(item, values.queryResults);
        case EventFrom.parrilla:
            index = getProductGridIndex(item as unknown as Product, values.filteredProductsIds);
            break;
        case EventFrom.productdetail:
            return values?.originItemListIndex || null;
        case EventFrom.shopcart:
            index = getItemListIndex<T>(item, values.cartItems as unknown as T[]);
            break;
        case EventFrom.wishList:
        case EventFrom.miniWishList:
            index = getItemListIndex<T>(item, values.wishListItems as unknown as T[]);
            break;
        case EventFrom.search:
            return values?.originItemListIndex;
        case EventFrom.fichaBundle:
            index = getItemListIndex<T>(item, values?.productPageProduct.bundleProducts as unknown as T[]);
            break;
        case EventFrom.cartYoda:
        case EventFrom.completeLook:
        case EventFrom.completeLookYoda:
        case EventFrom.frecuentlyBoughtTogether:
        case EventFrom.othersYouMayLike:
        case EventFrom.recentlyViewed:
        case EventFrom.relateds:
        case EventFrom.relatedsBuyed:
        case EventFrom.similar:
        case EventFrom.similaresYoda:
        case EventFrom.topSales:
            index = getRelatedProductIndex(
                item,
                eventData?.place,
                eventData?.relatedsSectionId,
                values?.relatedProductsPlaces
            );
            break;
        default:
            index = item && values.items ? getItemListIndex<T>(item, values.items as unknown as T[]) : -1;
            break;
    }
    return index === -1 ? null : index + 1;
};

/* ##########################################################  */

/**
 * TODO ECOMDUTI-22445 Locate massimo dutti page types
 *
 * @param page
 * @param eventFrom
 * @returns ZenitPageType
 */
export const getPageType = (values: ZenitEventValues, eventFrom: EventFrom, eventData: ZenitEventData): ZenitPageType => {
    if (eventData?.searchTerm !== '' && eventData?.searchTerm !== undefined) {
        return ZenitPageType.SEARCH_REQUEST;
    }
    if (values?.searchRequestQuery !== '' && values?.searchRequestQuery !== undefined) {
        return ZenitPageType.SEARCH_REQUEST;
    }
    // eslint-disable-next-line @typescript-eslint/no-extra-parens
    if (eventFrom === EventFrom.header || (eventFrom === EventFrom.relateds && eventData?.place === 'SEARCH')) {
        return ZenitPageType.PRODUCT_SEARCH_START;
    }
    if (eventFrom === EventFrom.searchResults) {
        return ZenitPageType.PRODUCT_SEARCH_RESULTS;
    }
    if (eventFrom === EventFrom['shop-cart-packagin']) {
        return ZenitPageType.GIFT_OPTIONS;
    }
    switch (values?.page) {
        case 'ItxAngularGiftCardPage':
            return ZenitPageType.GIFTCARD;
        case 'ItxAngularShopcartPage':
            return values.cartItems?.length ? ZenitPageType.SHOPPING_CART : ZenitPageType.SHOPPING_CART_EMPTY;
        case 'ItxCategoryPage':
            return ZenitPageType.PRODUCT_LIST;
        case 'ItxHomePage':
        case 'ItxHomeMenPage':
            return ZenitPageType.HOME;
        case 'ItxProductPage':
            return (values.item as Product)?.bundleProducts.length ? ZenitPageType.PRODUCT_BUNDLE_DETAILS
                : ZenitPageType.PRODUCT_DETAILS;
        case 'ItxAngularWishlistPage':
            return ZenitPageType.WISHLIST;
        case 'ItxAngularLookbookPage':
            return ZenitPageType.CATEGORY_VIEW;
        case 'ItxCheckoutShippingPage':
            return ZenitPageType.CHECKOUT_SHIPPING_METHOD;
        case 'ItxCheckoutShippingDeliveryPage':
            return ZenitPageType.CHECKOUT_HOME_ADDRESS_LIST;
        case 'ItxCheckoutShippingDeliveryAddressPage':
            return values.isSelectedAddress
                ? ZenitPageType.CHECKOUT_MODIFY_PERSONAL_DETAILS : ZenitPageType.CHECKOUT_ADD_PERSONAL_DETAILS;
        case 'ItxCheckoutShippingDeliverySummaryPage':
            return ZenitPageType.CHECKOUT_TYPE_DELIVERY_METHOD;
        case 'ItxCheckoutShippingStoreSummaryPage':
            return ZenitPageType.CHECKOUT_STORE_DETAIL;
        case 'ItxCheckoutShippingDropPointSummaryPage':
            return ZenitPageType.CHECKOUT_DROP_POINT_DETAIL;
        case 'ItxCheckoutShippingStorePage':
            return values.selectedViewPickup === 'map' ? ZenitPageType.CHECKOUT_STORE_COLLECTION_POINT_MAP : ZenitPageType.CHECKOUT_STORE_LIST;
        case 'ItxCheckoutShippingDropPointPage':
            return values.selectedViewPickup === 'map' ? ZenitPageType.CHECKOUT_DROP_POINT_MAP : ZenitPageType.CHECKOUT_DROP_POINT_LIST;
        case 'ItxCheckoutPaymentPage':
            return ZenitPageType.CHECKOUT_PAYMENT_METHOD;
        case 'ItxCheckoutSummaryPage':
            return ZenitPageType.CHECKOUT_ORDER_REVIEW;
        case 'ItxCheckoutRepaymentPage':
            return ZenitPageType.CHECKOUT_REPAYMENT;
        case 'ItxAngularOrderConfirmationPage':
        case 'ItxOrderConfirmationPage':
            return ZenitPageType.PURCHASE_ORDER_END;
        case 'ItxWorldWidePage':
            return ZenitPageType.WORLDWIDE;
        default:
            return ZenitPageType.UNKNOWN;
    }
};

export const getSection = (section: string): Section => {
    return Section[section];
};

/**
 * Identifier of the category
 * NOTE: If value cannot be sent use a code value of -1
 *
 * @param page
 * @param categoryId
 * @param categoryProductId
 * @returns number
 */
export const getCategoryId = (page: PageSection, categoryId: string, categoryProductId: string): number => {
    if (page === 'ItxProductPage') {
        return categoryProductId ? +categoryProductId : -1;
    }
    return categoryId ? +categoryId : -1; // Added + for number format
};

export const getCategoryURL = (zenitValues: ZenitEventValues): string => {
    const locationPath = {
        ItxProductPage: zenitValues.productCategoryUrl,
        ItxCategoryPage: zenitValues.categoryUrl,
        ItxAngularLookbookPage: zenitValues.categoryUrl,
    }[zenitValues.page];
    const categoryUrlSplit = locationPath ? locationPath.split('-') : undefined;
    return categoryUrlSplit ? categoryUrlSplit[categoryUrlSplit.length - 1] : null;
};

export const getCatentryId = (item: ItemTypes): number => {
    if (!item) {
        return null;
    }

    // Caso ficha bundle
    if ((item as Product).type === 'BundleBean') {
        return parseInt((item as Product).id);
    }

    if ('status' in item && item.status?.selectedColor?.catentryId) {
        return item.status.selectedColor.catentryId;
    }

    return null;
};

export const getPartNumber = (item: CartItem | GiftCard | Item | Product): string | null => {
    return ((item as Product)?.status || (item as CartItem))?.selectedSize?.partnumber || (item as GiftCard)?.partnumber
        || (item as CartItem)?.reference || null;
};

export const getReferenceStyle = (item: CartItem | GiftCard | Item | Product): string | -1 => {
    const itemStatus = (item as Product)?.status;

    if (itemStatus) return itemStatus?.selectedColor?.reference;
    if ((item as CartItem)?.reference) {
        const referenceSplit = (item as CartItem).reference.split('-');
        const mocacoStyle = referenceSplit[0]?.substring(0, 11);

        if (mocacoStyle) {
            return `C${mocacoStyle}-${referenceSplit[1]}`;
        }
    }

    return (item as GiftCard)?.partnumber || -1;
};

export const getSkus = (items: CartItem[] | Item[] | Product[]): string[] => {
    return items.map((item) => getPartNumber(item));
};

export const getCartId = (cartId: string): string => {
    return cartId === '-1' ? null : cartId;
};

export const getStoreId = (storeId: string): number => {
    return storeId ? parseInt(storeId) : null;
};

export const getCurrency = (currency: CurrencyInfo): string => {
    return currency?.currencyCode;
};

export const getItems = (items: CartItem[]): ZenitItem[] => {
    return items?.map((item: CartItem): ZenitItem => {
        return {
            amount: item?.unitPrice * item?.quantity,
            articleBrandCode: 11,
            brandId: 11,
            price: item?.unitPrice,
            section: getSection(item?.sectionNameEN),
            sku: getSkus([item])?.[0],
            units: item?.quantity,
        };
    });
};

export const getZoomLevel = (gridType: GridType): ZenitZoomLevel => {
    return {
        1: ZenitZoomLevel.ZOOM1,
        2: ZenitZoomLevel.ZOOM2,
        3: ZenitZoomLevel.ZOOM3,
    }[gridType];
};

export const getPersonalization = (
    item: ItemTypes,
    relatedProductsPlaces: Record<ProductViewPlace, RelatedsPlace>,
    place: ProductViewPlace,
    relatedsSectionId: string
): ZenitPersonalizationParam => {
    const relatedsData = getRelatedProduct(item, place, relatedsSectionId, relatedProductsPlaces);
    return {
        objective: relatedsData?.modelName,
        provider: relatedsData?.provider,
        strategy: relatedsData?.relatedType,
        version: relatedsData?.modelVersion,
    };
};

/** Obtiene el origen del evento según la página (page), ubicación (EventFrom), el tipo de (item) y la información del modal abierto */
export const getOrigin = (
    eventName: ZenitEventName,
    eventFrom: EventFrom,
    page: PageSection,
    item: ItemTypes,
    modalData: ModalData<any>,
    origin: EmpathySearcherOrigin | SearchType,
): OriginAddToCart | OriginPageHit => {
    // Caso PageHit con origin para vista buscador resultados
    if (origin && eventFrom === EventFrom.searchResults) return getSearchOrigin(origin);

    if (eventName === ZenitEventName.PageHit) return null;

    const eventFromData = eventFromParser(eventFrom, item, modalData);
    const eventFromParsed = eventFromData.modalPlace || eventFromData.eventFromParsed;
    const relatedsParser: Partial<Record<EventFrom, string>> = {
        cart_yoda: ZenitEventOrigin.list,
        completatulook_yoda: ZenitEventOrigin.list,
        completeLook: ZenitEventOrigin.list,
        frecuently_bought_together: ZenitEventOrigin.list,
        other_you_may_like: ZenitEventOrigin.list,
        productos_relacionados_comprados: ZenitEventOrigin.list,
        productos_relacionados: ZenitEventOrigin.list,
        recentlyViewed: ZenitEventOrigin.list,
        similar: ZenitEventOrigin.list,
        similares_yoda: ZenitEventOrigin.list,
        top_sales: ZenitEventOrigin.list,
    };

    return {
        ItxCategoryPage: {
            'size-recommender-component': ZenitEventOrigin.sizeRecommender,
            miniCart: ZenitEventOrigin.miniShopcart,
        }[eventFromParsed] || ZenitEventOrigin.list,
        ItxProductPage: {
            'size-recommender-component': ZenitEventOrigin.sizeRecommender,
            ficha_bundle: ZenitEventOrigin.list,
            miniCart: ZenitEventOrigin.increase,
            shopcart: ZenitEventOrigin.increase,
        }[eventFromParsed] || relatedsParser[eventFromParsed] || ZenitEventOrigin.mainCtaPdp,
        ItxAngularShopcartPage: {
            shopcart: ZenitEventOrigin.increase,
        }[eventFromParsed] || relatedsParser[eventFromParsed],
        ItxAngularWishlistPage: {
            wishList: ZenitEventOrigin.list,
        }[eventFromParsed] || relatedsParser[eventFromParsed],
        ItxAngularGiftCardPage: ZenitEventOrigin.list,
    }[page] || null;
};

export const getSearchList = (origin: EmpathySearcherOrigin | SearchType): ZenitList => {
    switch (origin) {
        case 'no_results':
            return ZenitList.noResultsFallback;
        case 'next_query_grid':
            return ZenitList.alternativeResults;
        case 'discovery_wall':
        case 'empathize_term':
        case 'empathize_trending':
        case 'trending':
            return ZenitList.suggestedProduct;
        default:
            return ZenitList.searchResults;
    }
};

export const getListForRelateds = (
    page: PageSection,
    modalPlace: EventFrom,
    eventFromParsed: EventFrom,
    place: ProductViewPlace,
): ZenitList => {
    const crossSelling: Record<PageSection, ZenitList> = {
        ItxAngularProductPage: ZenitList.crossSellingPdp,
        ItxAngularShopcartPage: ZenitList.crossSellingCart,
    };
    const isSearchCrossList = place === 'SEARCH' ? ZenitList.searchCrossCustom : undefined;
    const relatedsListParser = {
        // addToCartModal: undefined, // Si fuera necesario añadir casos para el modal de addToCart
        back_soon: ZenitList.crossSimilarBackSoon,
        coming_soon: ZenitList.crossSimilarComingSoon,
        completeLook: {
            ItxAngularShopcartPage: ZenitList.crossSellingCart,
        }[page] || ZenitList.crossSellingPdp,
        completatulook_yoda: crossSelling[page] || ZenitList.crossRecommendation,
        frecuently_bought_together: crossSelling[page] || ZenitList.crossRecommendation,
        other_you_may_like: ZenitList.mayAlsoLike,
        recentlyViewed: ZenitList.crossViewedRecently,
        productos_relacionados_comprados: crossSelling[page] || ZenitList.crossRecommendation,
        productos_relacionados: crossSelling[page] || ZenitList.crossRecommendation,
        similar: {
            coming_soon: ZenitList.crossSimilarComingSoon,
            back_soon: ZenitList.crossSimilarBackSoon,
        }[modalPlace] || ZenitList.crossSimilarPdp,
        top_sales: crossSelling[page] || ZenitList.crossRecommendation,
    }[eventFromParsed || modalPlace];

    return isSearchCrossList || relatedsListParser;
};

export const getListForPage = (
    eventFrom: EventFrom,
    sourceEventFromItem: EventFrom,
    originItemListName: ZenitList,
    eventName: ZenitEventName,
): ZenitList => {
    switch (eventFrom || sourceEventFromItem) {
        case 'parrilla':
        case 'giftcard':
            return ZenitList.grid;
        case 'ficha_bundle':
        case 'vista_rapida_bundle':
            return ZenitList.bundle;
        case 'miniWishList':
        case 'wishList':
            return ZenitList.wishlist;
        case 'shopcart':
        case 'miniCart':
            return ZenitList.shoppingCart;
        case 'directo':
            return ZenitList.noListUndefined;
        case 'searchResults':
            return ZenitList.searchResults;
        case 'productdetail':
        case 'search':
            return originItemListName || ZenitList.noListUndefined;
        default:
            return eventName === ZenitEventName.PageHit ? null : ZenitList.noListUndefined;
    }
};

export const getList = (
    eventFrom: EventFrom,
    page: PageSection,
    modalData: ModalData<any>,
    item: ItemTypes,
    eventName: ZenitEventName,
    originData: {
        originItemListName?: ZenitList;
        place?: ProductViewPlace;
        searchOrigin?: EmpathySearcherOrigin | SearchType;
        sourceEventFromItem?: EventFrom;
    }
): ZenitList => {
    const { eventFromParsed, modalPlace } = eventFromParser(eventFrom || originData.sourceEventFromItem, item, modalData);
    if (RelatedPlaces.includes(eventFromParsed)) {
        return getListForRelateds(page, modalPlace, eventFromParsed, originData?.place);
    }

    if (originData.searchOrigin) return getSearchList(originData.searchOrigin);

    return getListForPage(eventFrom, originData.sourceEventFromItem, originData.originItemListName, eventName);
};

export const getIndex = (position: string): number => {
    return position ? parseInt(position) : 0;
};

// ############### EXPERIMENTS PROPERTY LOGIC ###############

export const getOptimizelyExperimentCampaignId = (campaigns: OptimizelyCampaignsState, experimentId: string): string => {
    return Object.values(campaigns).find((campaing) => {
        return campaing.experiment.id === experimentId;
    })?.id;
};

export const formOptimizelyExperimentObject = (
    experiment: OptimizelyExperiment,
    data: OptimizelyData,
    campaigns: OptimizelyCampaignsState,
    experimentId: string,
): ZenitExperiment => {
    const optimizelyExperimentCampaignId = getOptimizelyExperimentCampaignId(campaigns, experimentId);

    return {
        experimentDetails: {
            experimentAccountId: parseInt(data.accountId),
            experimentCampaignId: parseInt(optimizelyExperimentCampaignId),
            experimentId: parseInt(experiment.id),
            experimentProjectId: parseInt(data.projectId),
        },
        experimentKey: experiment.experimentName,
        experimentName: experiment.experimentName,
        provider: 'optimizely',
        variationKey: experiment.variation.name,
    };
};

export const getOptimizelyExperiments = (commonWindow: CommonWindow): ZenitExperiment[] => {
    if (!commonWindow?.optimizely?.initialized) {
        return [];
    }

    const data = commonWindow.optimizely.get('data') as OptimizelyData;
    const state = commonWindow.optimizely.get('state') as OptimizelyState;
    const campaigns = state.getCampaignStates({ isActive: true });
    const stateExperiments = state.getExperimentStates({ isActive: true });

    return Object.entries(stateExperiments).reduce((acc: ZenitExperiment[], [experimentId, val]) => {
        acc.push(formOptimizelyExperimentObject(val, data, campaigns, experimentId));
        return acc;
    }, []);
};

/** Si en el futuro se incluyen más proveedores se añadirán aquí */
export const getExperiments = (commonWindow: CommonWindow): ZenitExperiments => {
    const optimizely = getOptimizelyExperiments(commonWindow);

    return { optimizely };
};

// --------------- END EXPERIMENTS PROPERTY LOGIC ---------------
export const getRelatedsProvider = (originItemRelatedType: EventFrom): RelatedsProvider => {
    return {
        completatulook_yoda: 'YODA',
        completeLook: 'wideyes',
        frecuently_bought_together: 'tinybird',
        other_you_may_like: 'YODA',
        productos_relacionados: 'tinybird',
        similar: 'wideyes',
        top_sales: 'tinybird',
    }[originItemRelatedType];
};

export const getClickOrigin = (
    eventFrom: EventFrom,
    page: PageSection,
    modalData: ModalData<any>,
    item: ItemTypes,
    eventName: ZenitEventName,
    originData: {
        originItemRelatedType: EventFrom;
        originItemListName: ZenitList;
        searchOrigin: EmpathySearcherOrigin | SearchType;
        sourceEventFromItem: EventFrom;
    }
): ZenitClickOrigin => {
    const list = getList(
        eventFrom,
        page,
        modalData,
        item,
        eventName,
        {
            originItemListName: originData.originItemListName,
            searchOrigin: originData.searchOrigin,
            sourceEventFromItem: originData.sourceEventFromItem,
        }
    );
    const provider = getRelatedsProvider(originData.originItemRelatedType);
    return {
        list,
        provider,
        strategy: originData.originItemRelatedType,
    };
};

export const getSearchRequestFilters = (_filters: string): SearchRequestFilter[] => {
    if (!_filters) {
        return [];
    }

    const filters: Record<string, string[]> = _filters.split(',').reduce((acc, curr) => {
        const [_key, value] = curr.split(':');
        const key = _key.trim();

        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(value);
        return acc;
    }, {});

    return Object.entries(filters).map(([name, values]) => ({
        name: name.split('_')?.[0].toUpperCase(),
        values,
        numElements: values.length,
    }));
};

export const getSearchRequestOffset = (searchRequestOffset: number = 0): number => {
    if (searchRequestOffset === 0) {
        return 0;
    }

    return searchRequestOffset - limitQuerySearch;
};

export const getSearchRequestParams = (
    param: string,
    values: ZenitEventValues,
    eventData: ZenitEventData,
): number | string | SearchRequestFilter[] | null => {
    switch (param) {
        case 'searchRequestResponseTime':
            return eventData.responseTime ?? null;
        case 'searchRequestResultsNum': {
            const resultsNum = parseInt(eventData?.totalHits?.toString());
            return Number.isNaN(resultsNum) ? null : resultsNum;
        }
        case 'searchRequestMethodName':
            return SearchRequestMethodName.query;
        case 'searchRequestLimit':
            return limitQuerySearch;
        case 'searchRequestHTTPResponseCode':
            return eventData.responseCode ?? -1;
        case 'searchRequestOffset':
            return getSearchRequestOffset(values.searchRequestOffset);
        case 'searchRequestProvider':
            return eventData.provider;
        case 'searchRequestPass':
            return eventData.searchPass;
        case 'searchRequestFilters':
            return getSearchRequestFilters(values.searchRequestFilters);
    }
};
